import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import Container from 'components/Container';
import Headline from 'components/Headline';
import LoanOfferConfirmation from 'components/LoanOfferConfirmation';
import {
  newCheckInAction,
  newNextAction,
} from '../../actions/navigation';

// Components
import Header from '../../components/Header';
import Progress from '../../components/Progress';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Transition from '../../components/Transition';
import ApplicationId from '../../components/ApplicationId';

// Configuration & assets
import { useConfig } from '../../config/ConfigContext';

let hardpullWebsocket = null;
let hardpullEndProcessTimer = null;
const NORMAL_CLOSE = 'Normal close';

const Confirmed = (props) => {
  const config = useConfig();
  const { navigate, checkinData, params, searchParams } = props;
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(false);
  const hasDownPayment = !!get(checkinData, 'data.data.downPaymentRequired');
  const workflowType = get(params, 'workflowType') || 'text2apply';
  const dpOnlyApp = workflowType === 'text2apply-dponly';
  const lusaPlusApp = workflowType === 'text2apply';
  const key = searchParams.get('key');

  const createHardpullWebsocket = (ssnLastFour) => {
    const appId = checkinData.attributes.entityId;
    hardpullWebsocket = new WebSocket(`${config.idScanWebsocketUrl}?application_id=${appId}`);
    console.log('Hardpull connection:', hardpullWebsocket);

    hardpullWebsocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'OfferConfirmation' && checkinData.activity !== 'SignLoanDocument') {
        console.log('eventData:', event.data);
        hardpullWebsocket && hardpullWebsocket.close(1000, NORMAL_CLOSE);
        hardpullWebsocket = null;
        clearTimeout(hardpullEndProcessTimer);
        props.dispatch(newCheckInAction({
          key,
          workflowType,
          success: (res) => {
            console.log('res:', res);
            if (get(res, 'activity') === 'WelcomeBack') {
              props.dispatch(newNextAction({
                key,
                workflowType,
                data: { SsnLast4Digits: ssnLastFour },
                success: (res1) => {
                  setIsProgressVisible(false);
                  props.navigate(get(res1, 'data.url').replace(config.hostUrl, ''));
                },
                fail: (error) => {
                  setIsProgressVisible(false);
                  console.log('failed', error);
                },
              }));
            }
          },
        }));
      }
    });

    hardpullWebsocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        createHardpullWebsocket(ssnLastFour);
      }
    });
  };

  useEffect(() => {
    if (get(checkinData, ['data', 'data']) === undefined) {
      navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
    } else {
      createHardpullWebsocket(get(checkinData, ['data', 'data', 'ssnLastFour']));
    }
  }, []);

  const hardpullEndProcess = () => {
    hardpullWebsocket && hardpullWebsocket.close(1000, NORMAL_CLOSE);
    hardpullWebsocket = null;
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const { dispatch } = props;

    hardpullEndProcessTimer = setTimeout(() => {
      hardpullEndProcess();
    }, get(config, 'verificationIntervals'));

    setIsProgressVisible(true);

    dispatch(newNextAction({
      key,
      workflowType,
      data: {
        returnUrl: `${window.location.origin}/agreement/complete?key=${key}`,
      },
      onUploadProgress: (progressEvent) => {
        setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
      },
      success: (res) => {
        if (get(res, 'data.url')) {
          setIsProgressVisible(false);
          hardpullWebsocket && hardpullWebsocket.close(1000, NORMAL_CLOSE);
          hardpullWebsocket = null;
          clearTimeout(hardpullEndProcessTimer);
          navigate(get(res, 'data.url').replace(config.hostUrl, ''));
        }
      },
      fail: (error) => {
        console.log('error', error);
      },
    }));
  };

  return (
    <Transition className="transition-item wrapper loan-options">
      <Header />
      <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
      <div className="text-center">
        <Headline
          header={(
            <>
              Offer Confirmation
            </>
          )}
        />
        <Container className="container">
          <FormRow>
            <FormColumn>
              {get(checkinData, 'data.data') && <LoanOfferConfirmation loanDetails={get(checkinData, 'data.data')} handleSubmitFrom={handleSubmitFrom} isDPOnly={dpOnlyApp} isLUSAplus={lusaPlusApp} />}
            </FormColumn>
          </FormRow>
        </Container>
      </div>
      {hasDownPayment ? (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20 }}>
              <small>
                By clicking “CONTINUE TO ESIGN”, your Debit Card will be charged the down payment.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      ) : null}
      <FormRow>
        <FormColumn>
          <div style={{ marginTop: 20 }}>
            <small>
              Before your application can be finalized, LendingUSA and/or one of its
              {' '}
              <a href="https://lendingusa.com/lending-partners/" style={{ textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">lending partners</a>
              {' '}
              will request a full credit report from one or more of the credit reporting agencies. This request will occur if you proceed past this point in the application. This request will appear as a hard inquiry on your credit report and may affect your credit score.
            </small>
          </div>
        </FormColumn>
      </FormRow>
      {!!Number(get(checkinData, 'data.data.zipTerm')) && (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20 }}>
              <small>
                * Your loan may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on a monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan. Length of Promotional Periods vary, please review your loan agreement for full details.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      )}
      {((get(checkinData, 'data.data.originationPercentage') !== 0) && !dpOnlyApp) && (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20, paddingBottom: 20 }}>
              <small>
                ** The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer listed above by multiplying the Amount Financed figure for that offer by .08 if the Origination Fee is 8%, by .05 if it is 5%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      )}
      {hasDownPayment ? (
        <FormRow>
          <FormColumn>
            <div style={{ marginTop: 20, paddingBottom: 20 }}>
              <small>
                <sup>1</sup>
                &nbsp;In connection with your intended purchase, the merchant requires a down payment in the amount shown above. Your down payment to the merchant for your purchase is a condition to the fulfilment of your loan offer.
              </small>
            </div>
          </FormColumn>
        </FormRow>
      ) : null}
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

Confirmed.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  checkinData: state.screen.checkinData,
});

export default connect(mapStateToProps)(Confirmed);
